import { useEffect, useRef, useState } from 'react';
import { LocalVideoTrack, RemoteVideoTrack } from 'livekit-client';

/**
 * Sem isto voces vao discutir qualidade no escuro ("ta ruim pra voces tambem?").
 * Mostra o que esta acontecendo de verdade: resolucao, fps, bitrate e se o
 * navegador esta limitando por CPU ou por banda.
 */

interface Sample {
  label: string;
  fps: number;
  width: number;
  height: number;
  kbps: number;
  limitation?: string;
  dropped?: number;
  lost?: number;
}

/** Estado do tick anterior, para derivar taxas. */
interface Prev {
  bytes: number;
  frames: number;
  at: number;
}

function rate(current: number, prev: Prev | null, now: number, field: 'bytes' | 'frames'): number {
  if (!prev) return 0;
  const dt = (now - prev.at) / 1000;
  if (dt <= 0) return 0;
  return (current - prev[field]) / dt;
}

export function Stats({ track }: { track: LocalVideoTrack | RemoteVideoTrack }) {
  const [sample, setSample] = useState<Sample | null>(null);
  const prev = useRef<Prev | null>(null);

  useEffect(() => {
    let alive = true;
    prev.current = null;

    const tick = async () => {
      try {
        const now = performance.now();

        if (track instanceof LocalVideoTrack) {
          const all = await track.getSenderStats();
          if (!all?.length) return;
          // Com simulcast vem uma entrada por camada; a maior e a que interessa.
          const s = all.reduce((a, b) => (b.frameWidth > a.frameWidth ? b : a));

          const bytes = s.bytesSent ?? 0;
          const kbps = (rate(bytes, prev.current, now, 'bytes') * 8) / 1000;
          prev.current = { bytes, frames: s.framesSent, at: now };

          if (!alive) return;
          setSample({
            label: 'enviando',
            fps: Math.round(s.framesPerSecond ?? 0),
            width: s.frameWidth,
            height: s.frameHeight,
            kbps: Math.round(kbps),
            limitation:
              s.qualityLimitationReason && s.qualityLimitationReason !== 'none'
                ? s.qualityLimitationReason
                : undefined,
          });
        } else {
          const s = await track.getReceiverStats();
          if (!s) return;

          const bytes = s.bytesReceived ?? 0;
          const kbps = (rate(bytes, prev.current, now, 'bytes') * 8) / 1000;
          // O lado receptor nao reporta framesPerSecond; derivamos dos decodificados.
          const fps = rate(s.framesDecoded, prev.current, now, 'frames');
          prev.current = { bytes, frames: s.framesDecoded, at: now };

          if (!alive) return;
          setSample({
            label: 'recebendo',
            fps: Math.round(fps),
            width: s.frameWidth ?? 0,
            height: s.frameHeight ?? 0,
            kbps: Math.round(kbps),
            dropped: s.framesDropped,
            lost: s.packetsLost,
          });
        }
      } catch {
        /* stats indisponiveis neste navegador */
      }
    };

    void tick();
    const id = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [track]);

  if (!sample) return null;

  const mbps = (sample.kbps / 1000).toFixed(1);
  // Primeiro segundo ainda nao tem taxa; so alerta depois que ha medida real.
  const warn = sample.kbps > 0 && sample.fps > 0 && sample.fps < 24;

  return (
    <div className="stats" data-warn={warn || undefined}>
      <span>{sample.label}</span>
      <span>
        {sample.width}&times;{sample.height}
      </span>
      <span>{sample.fps} fps</span>
      <span>{mbps} Mb/s</span>
      {sample.limitation && <span className="stats-warn">limitado: {sample.limitation}</span>}
      {sample.dropped ? <span>{sample.dropped} quadros perdidos</span> : null}
      {sample.lost ? <span>{sample.lost} pacotes perdidos</span> : null}
    </div>
  );
}
